import type { IdentityClaims } from './Identity.Helper';

declare global {
    interface Window {
        /**
         * DEV ONLY (`npm run dev`): a bearer token for Ark.Core (a user token for `api://orbitalwatch-ark/Access`, e.g.
         * `orbital auth token --for ark`), set by Playwright (`page.addInitScript`) or by hand in the browser console
         * before the app loads. Ignored by production builds (`import.meta.env.DEV` is `false` there).
         */
        __DEV_TOKEN__?: string;
    }
}

/**
 * Development-only token injection, so the local Vite app can talk to a real Ark.Core (QA) with an injected token instead
 * of the interactive Entra External ID sign-in, which cannot be automated.
 *
 * Sources, in order: `window.__DEV_TOKEN__`, then `VITE_DEV_TOKEN` (process env / `.env.local`, never committed).
 * `import.meta.env.DEV` is replaced by the literal `false` in `vite build`, so the whole branch - including any
 * `VITE_DEV_TOKEN` value - is dead code and removed from production bundles; `vite.config.ts` additionally refuses to
 * build when `VITE_DEV_TOKEN` is set.
 *
 * Where it plugs in: `IdentityService` (access token, claims, login/logout), `RequireAuth` (counts as signed in; the
 * `/api/gate/verifysession` check still runs with the token) and `useIdentityUserDisplayName`. MSAL stays initialised
 * and simply has no account.
 */
export function getDevToken(): string | null {
    if (!import.meta.env.DEV) return null;
    const fromWindow = typeof window !== 'undefined' ? window.__DEV_TOKEN__ : undefined;
    const token = (fromWindow ?? import.meta.env.VITE_DEV_TOKEN ?? '').trim();
    return token.length > 0 ? token : null;
}

export function isDevTokenActive(): boolean {
    return getDevToken() !== null;
}

/** Claims of the injected token (best effort: an opaque or fake token yields a placeholder). */
export function getDevClaims(): IdentityClaims | null {
    const token = getDevToken();
    if (!token) return null;
    try {
        const payload = token.split('.')[1];
        if (!payload) throw new Error('not a JWT');
        const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(payload.length / 4) * 4, '='));
        const claims = JSON.parse(json) as IdentityClaims & { upn?: string; unique_name?: string };
        const email = claims.email ?? claims.emails?.[0] ?? claims.preferred_username ?? claims.upn ?? claims.unique_name;
        return { ...claims, email, name: claims.name ?? email ?? 'Developer (injected token)' };
    } catch {
        return { name: 'Developer (injected token)', email: 'dev@localhost' } as IdentityClaims;
    }
}
